import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Switch } from '../../ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';

const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const toDateInput = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : '');

// The admin types a wall-clock time in a named zone; we convert it to the actual instant
const zonedToUtc = (local, timeZone) => {
  if (!local) return null;
  const naive = new Date(`${local}:00Z`);
  const guess = new Date(naive.getTime());
  const asZone = new Date(guess.toLocaleString('en-US', { timeZone }));
  const asUtc = new Date(guess.toLocaleString('en-US', { timeZone: 'UTC' }));
  return new Date(naive.getTime() + (asUtc.getTime() - asZone.getTime())).toISOString();
};

const TIMEZONES = ['Europe/Lisbon', 'Europe/London', 'Europe/Madrid', 'UTC'];

export const RecognitionEditorDialog = ({ open, entry, categories, onClose, onSave }) => {
  const [form, setForm] = useState({});
  const [publishLocal, setPublishLocal] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      organization_name: entry?.organization_name || '',
      logo_url: entry?.logo_url || '',
      category: entry?.category || 'media_coverage',
      description: entry?.description || '',
      reference_url: entry?.reference_url || '',
      recognition_date: entry?.recognition_date || null,
      display_order: entry?.display_order ?? 0,
      featured_home: entry?.featured_home ?? true,
      status: entry?.status || 'draft',
      publish_timezone: entry?.publish_timezone || 'Europe/Lisbon',
      archived: entry?.archived || false,
    });
    setPublishLocal(entry?.publish_at ? toLocalInput(entry.publish_at) : '');
  }, [open, entry]);

  const set = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.organization_name?.trim()) {
      toast.error('Organization name is required');
      return;
    }
    if (form.status === 'scheduled' && !publishLocal) {
      toast.error('A scheduled entry needs a publication date and time');
      return;
    }
    setSaving(true);
    try {
      await onSave({
        ...form,
        recognition_date: form.recognition_date || null,
        publish_at: form.status === 'scheduled' ? zonedToUtc(publishLocal, form.publish_timezone) : null,
      });
      onClose();
    } catch {
      // the caller surfaces the error
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="recognition-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {entry ? 'Edit recognition' : 'New recognition'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-5">
          <div>
            <Label htmlFor="rec-name">Organization name</Label>
            <Input
              id="rec-name"
              value={form.organization_name || ''}
              onChange={(e) => set('organization_name')(e.target.value)}
              placeholder="Portugal Fintech"
              className="mt-1.5"
              data-testid="recognition-name-input"
            />
          </div>

          <div>
            <Label htmlFor="rec-logo">Logo URL (SVG or transparent PNG)</Label>
            <Input
              id="rec-logo"
              value={form.logo_url || ''}
              onChange={(e) => set('logo_url')(e.target.value)}
              placeholder="https://…/logo.svg"
              className="mt-1.5"
              data-testid="recognition-logo-input"
            />
            <p className="text-xs text-foreground-muted mt-1">
              Leave empty and the strip shows the organization name as a wordmark instead.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={set('category')}>
                <SelectTrigger className="mt-1.5" data-testid="recognition-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="rec-date">Recognition date</Label>
              <Input
                id="rec-date"
                type="date"
                value={toDateInput(form.recognition_date)}
                onChange={(e) => set('recognition_date')(e.target.value ? `${e.target.value}T12:00:00.000Z` : null)}
                className="mt-1.5"
                data-testid="recognition-date-input"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="rec-desc">Short description</Label>
            <Textarea
              id="rec-desc"
              value={form.description || ''}
              onChange={(e) => set('description')(e.target.value)}
              rows={2}
              placeholder="Featured in the Portugal Fintech Report 2026"
              className="mt-1.5"
              data-testid="recognition-description-input"
            />
            <p className="text-xs text-foreground-muted mt-1">
              Shown as the hover tooltip. State the relationship precisely - a feature is not an endorsement.
            </p>
          </div>

          <div>
            <Label htmlFor="rec-url">External reference URL</Label>
            <Input
              id="rec-url"
              value={form.reference_url || ''}
              onChange={(e) => set('reference_url')(e.target.value)}
              placeholder="https://…"
              className="mt-1.5"
              data-testid="recognition-url-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4 pt-1 border-t border-black/[0.06]">
            <div className="pt-4">
              <Label>Publication status</Label>
              <Select value={form.status} onValueChange={set('status')}>
                <SelectTrigger className="mt-1.5" data-testid="recognition-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="pt-4">
              <Label htmlFor="rec-order">Display order</Label>
              <Input
                id="rec-order"
                type="number"
                value={form.display_order ?? 0}
                onChange={(e) => set('display_order')(Number(e.target.value))}
                className="mt-1.5"
                data-testid="recognition-order-input"
              />
            </div>
          </div>

          {form.status === 'scheduled' && (
            <div className="grid grid-cols-2 gap-4 p-4 rounded-xl bg-[rgb(var(--background))]">
              <div>
                <Label htmlFor="rec-publish">Goes live at</Label>
                <Input
                  id="rec-publish"
                  type="datetime-local"
                  value={publishLocal}
                  onChange={(e) => setPublishLocal(e.target.value)}
                  className="mt-1.5"
                  data-testid="recognition-publish-at-input"
                />
              </div>
              <div>
                <Label>Timezone</Label>
                <Select value={form.publish_timezone} onValueChange={set('publish_timezone')}>
                  <SelectTrigger className="mt-1.5" data-testid="recognition-timezone-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map((tz) => (
                      <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="col-span-2 text-xs text-foreground-muted">
                Stays invisible on the site until that exact moment - use this for embargoed coverage.
              </p>
            </div>
          )}

          <div className="flex items-center justify-between pt-2 border-t border-black/[0.06]">
            <div>
              <p className="text-sm font-medium">Featured on homepage</p>
              <p className="text-xs text-foreground-muted">Up to six live entries appear in the strip.</p>
            </div>
            <Switch
              checked={!!form.featured_home}
              onCheckedChange={set('featured_home')}
              data-testid="recognition-featured-switch"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="border-black/10">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-primary hover:bg-primary-hover"
              data-testid="recognition-save-button"
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
